<template>
  <div class="mia-page" :class="{ 'mia-page--rtl': isRtl }">
    <!-- Hero Section -->
    <div class="mia-hero">
      <div class="mia-hero__content">
        <div class="mia-hero__icon">
          <i class="fas fa-cat" style="font-size: 40px; color: #ffffffff"></i>
        </div>
        <h1 class="mia-hero__title">{{ t("mia_greeting") }}</h1>
        <p class="mia-hero__subtitle">{{ t("mia_how_can_i_help") }}</p>
      </div>
      <div class="mia-hero__decoration">
        <div class="mia-hero__circle mia-hero__circle--1"></div>
        <div class="mia-hero__circle mia-hero__circle--2"></div>
        <div class="mia-hero__circle mia-hero__circle--3"></div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="mia-dashboard" :class="{ 'mia-dashboard--rtl': isRtl }">
      <!-- Feature Cards Section -->
      <section class="mia-section">
        <div class="mia-section__header">
          <h3 class="mia-section__title">
            ✨ {{ t("discover_mia_features") }}
          </h3>
          <button
            class="mia-section__info-btn"
            @click="showInfoToast"
            :title="t('long_press_to_learn_more')"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
        </div>
        <div class="mia-features-grid">
          <div
            v-for="feature in features"
            :key="feature.type"
            class="mia-feature-card"
            @click="navigateToFeature(feature)"
            @contextmenu.prevent="showFeatureDialog(feature)"
          >
            <div class="mia-feature-card__icon" v-html="feature.icon"></div>
            <span class="mia-feature-card__title">{{ feature.title }}</span>
          </div>
        </div>
      </section>

      <!-- Input Type Section -->
      <section class="mia-section">
        <h3 class="mia-section__title">
          🗂️ {{ t("pick_a_source_to_get_started") }}
        </h3>
        <div class="mia-input-grid">
          <div
            v-for="inputType in inputTypes"
            :key="inputType.value"
            class="mia-input-card"
            @click="navigateToCreate(inputType.value)"
          >
            <div class="mia-input-card__icon" v-html="inputType.icon"></div>
            <span class="mia-input-card__title">{{ inputType.label }}</span>
          </div>
        </div>
      </section>

      <!-- Recent Operations Section -->
      <section class="mia-section">
        <h3 class="mia-section__title">📝 {{ t("mia_recent_operations") }}</h3>
        <div class="mia-operations">
          <!-- Guest Mode -->
          <div v-if="isGuest" class="mia-guest-card">
            <img
              src="/images/work-orientation.png"
              alt="Get Started"
              class="mia-guest-card__image"
            />
            <h4 class="mia-guest-card__title">
              {{ t("guest_onboarding_title") }}
            </h4>
            <p class="mia-guest-card__text">
              {{ t("guest_onboarding_subtitle") }}
            </p>
            <button class="mia-guest-card__btn" @click="navigateToLogin">
              {{ t("sign_in") }}
            </button>
          </div>

          <!-- Loading State -->
          <div v-else-if="operationsLoading" class="mia-operations__loading">
            <div class="mia-spinner"></div>
            <span>{{ t("loading") }}</span>
          </div>

          <!-- Error State -->
          <div v-else-if="operationsError" class="mia-operations__error">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ef4444"
              stroke-width="1.5"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p>{{ operationsError }}</p>
            <button class="mia-btn mia-btn--primary" @click="fetchOperations">
              {{ t("try_again") }}
            </button>
          </div>

          <!-- Empty State -->
          <div
            v-else-if="operations.length === 0"
            class="mia-operations__empty"
          >
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#9ca3af"
              stroke-width="1"
            >
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14,2 14,8 20,8" />
            </svg>
            <p>{{ t("no_operations_yet") }}</p>
            <span>{{ t("create_first_content") }}</span>
          </div>

          <!-- Operations List -->
          <div v-else class="mia-operations__list">
            <div
              v-for="operation in operations"
              :key="operation.id"
              class="mia-operation-item"
              @click="navigateToOperation(operation)"
            >
              <div
                class="mia-operation-item__icon"
                v-html="getOperationIcon(operation.type)"
              ></div>
              <div class="mia-operation-item__content">
                <span class="mia-operation-item__title">{{
                  operation.title
                }}</span>
                <span class="mia-operation-item__date">{{
                  formatDate(operation.created_at || operation.createdAt)
                }}</span>
              </div>
              <span
                class="mia-operation-item__status"
                :class="`mia-operation-item__status--${operation.status}`"
              >
                {{ operation.status }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <!-- Feature Dialog -->
      <div
        v-if="showDialog"
        class="mia-dialog-overlay"
        @click.self="closeDialog"
      >
        <div class="mia-dialog">
          <div class="mia-dialog__icon" v-html="selectedFeature?.icon"></div>
          <h3 class="mia-dialog__title">{{ selectedFeature?.title }}</h3>
          <ul class="mia-dialog__list">
            <li
              v-for="(desc, index) in selectedFeature?.descriptions"
              :key="index"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="#22c55e"
                stroke="none"
              >
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
              {{ desc }}
            </li>
          </ul>
          <div class="mia-dialog__actions">
            <button
              class="mia-btn mia-btn--primary mia-btn--gradient"
              @click="createWithFeature"
            >
              {{ t("create") }}
            </button>
            <button
              class="mia-btn mia-btn--icon"
              @click="navigateToFeatureList"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useMiaStore } from "../stores/miaStore";
import { MiaFeatureType, MiaInputType, OperationTypeMapping } from "../types";
import { useI18n } from "../i18n";

export default {
  name: "MiaDashboard",
  components: {},
  setup() {
    const router = useRouter();
    const miaStore = useMiaStore();
    const { t, isRtl } = useI18n();

    const showDialog = ref(false);
    const selectedFeature = ref(null);

    const features = computed(() => [
      {
        type: MiaFeatureType.PROBLEM_SOLVING,
        title: t("problem_solving"),
        icon: `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M9 3h6M10 3v6.5L5 19h14l-5-9.5V3" />
        </svg>`,
        descriptions: [
          t("problem_solving_desc_1"),
          t("problem_solving_desc_2"),
          t("problem_solving_desc_3"),
          t("problem_solving_desc_4"),
        ],
        route: "/mia/problems",
      },
      {
        type: MiaFeatureType.QBANK,
        title: t("qbank"),
        icon: `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>`,
        descriptions: [
          t("qbank_desc_1"),
          t("qbank_desc_2"),
          t("qbank_desc_3"),
          t("qbank_desc_4"),
        ],
        route: "/mia/qbanks",
      },
      {
        type: MiaFeatureType.SUMMARY,
        title: t("summaries"),
        icon: `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14,2 14,8 20,8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>`,
        descriptions: [
          t("summaries_desc_1"),
          t("summaries_desc_2"),
          t("summaries_desc_3"),
          t("summaries_desc_4"),
        ],
        route: "/mia/summaries",
      },
      {
        type: MiaFeatureType.FLASH_CARDS,
        title: t("flash_cards"),
        icon: `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <polygon points="12,2 2,7 12,12 22,7 12,2" />
          <polyline points="2,17 12,22 22,17" />
          <polyline points="2,12 12,17 22,12" />
        </svg>`,
        descriptions: [
          t("flash_cards_desc_1"),
          t("flash_cards_desc_2"),
          t("flash_cards_desc_3"),
          t("flash_cards_desc_4"),
        ],
        route: "/mia/flash-cards",
      },
    ]);

    const inputTypes = computed(() => [
      {
        value: MiaInputType.TEXT,
        label: t("text"),
        icon: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <polyline points="4,7 4,4 20,4 20,7" />
          <line x1="9" y1="20" x2="15" y2="20" />
          <line x1="12" y1="4" x2="12" y2="20" />
        </svg>`,
      },
      {
        value: MiaInputType.IMAGE,
        label: t("image"),
        icon: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21,15 16,10 5,21" />
        </svg>`,
      },
      {
        value: MiaInputType.AUDIO,
        label: t("audio"),
        icon: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
          <path d="M19 10v2a7 7 0 01-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>`,
      },
      {
        value: MiaInputType.FILE,
        label: t("file"),
        icon: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14,2 14,8 20,8" />
        </svg>`,
      },
    ]);

    const isGuest = computed(() => !localStorage.getItem("access_token"));
    const operations = computed(() => miaStore.state.operations);
    const operationsLoading = computed(() => miaStore.state.operationsLoading);
    const operationsError = computed(() => miaStore.state.operationsError);

    const fetchOperations = async () => {
      if (!isGuest.value) {
        await miaStore.fetchOperations();
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const getOperationIcon = (type) => {
      const icons = {
        ai_problem: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9 3h6M10 3v6.5L5 19h14l-5-9.5V3" />
        </svg>`,
        question_bank: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>`,
        file_processing: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14,2 14,8 20,8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>`,
        flash_card_deck: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polygon points="12,2 2,7 12,12 22,7 12,2" />
          <polyline points="2,17 12,22 22,17" />
          <polyline points="2,12 12,17 22,12" />
        </svg>`,
      };
      return icons[type] || icons.ai_problem;
    };

    const navigateToFeature = (feature) => {
      router.push(feature.route);
    };

    const navigateToCreate = (inputType) => {
      router.push({ path: "/mia/create", query: { inputType } });
    };

    const navigateToOperation = (operation) => {
      const isProcessing =
        operation.status === "pending" || operation.status === "processing";
      const isCompleted = operation.status === "completed";

      switch (operation.type) {
        case "flash_card_deck":
          if (isCompleted) {
            router.push({
              path: "/mia/flash-cards/study",
              query: { deckId: operation.id },
            });
          } else {
            router.push({
              path: "/mia/flash-cards/details",
              query: { deckId: operation.id },
            });
          }
          break;
        case "ai_problem":
          if (operation.latest_output_id || operation.latestOutputId) {
            router.push({
              path: "/mia/problems/details",
              query: {
                problemId: operation.id,
                solutionId:
                  operation.latest_output_id || operation.latestOutputId,
              },
            });
          }
          break;
        case "file_processing":
          router.push({
            path: "/mia/summaries/details",
            query: { id: operation.id },
          });
          break;
        case "question_bank":
          if (operation.latest_output_id || operation.latestOutputId) {
            router.push({
              path: "/mia/qbanks/details",
              query: {
                bankId: operation.latest_output_id || operation.latestOutputId,
              },
            });
          }
          break;
      }
    };

    const navigateToLogin = () => {
      router.push("/login");
    };

    const showInfoToast = () => {
      alert(t("long_press_to_learn_more"));
    };

    const showFeatureDialog = (feature) => {
      selectedFeature.value = feature;
      showDialog.value = true;
    };

    const closeDialog = () => {
      showDialog.value = false;
      selectedFeature.value = null;
    };

    const createWithFeature = () => {
      closeDialog();
      router.push({
        path: "/mia/create",
        query: { feature: selectedFeature.value?.type },
      });
    };

    const navigateToFeatureList = () => {
      closeDialog();
      router.push(selectedFeature.value?.route);
    };

    onMounted(() => {
      fetchOperations();
    });

    return {
      features,
      inputTypes,
      isGuest,
      operations,
      operationsLoading,
      operationsError,
      showDialog,
      selectedFeature,
      t,
      isRtl,
      fetchOperations,
      formatDate,
      getOperationIcon,
      navigateToFeature,
      navigateToCreate,
      navigateToOperation,
      navigateToLogin,
      showInfoToast,
      showFeatureDialog,
      closeDialog,
      createWithFeature,
      navigateToFeatureList,
    };
  },
};
</script>

<style scoped>
/* ========== MIA DASHBOARD - CLEAN DESIGN ========== */
@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.2;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* ========== Page Container ========== */
.mia-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fffe 0%, #e8fff1 100%);
}

.mia-page--rtl {
  direction: rtl;
  text-align: right;
}

/* ========== Hero Section ========== */
.mia-hero {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 50%, #008f32 100%);
  padding: 60px 20px;
  position: relative;
  overflow: hidden;
  border-radius: 0 0 50px 50px;
  margin-bottom: 40px;
  box-shadow: 0 10px 40px rgba(0, 199, 68, 0.3);
}

.mia-hero__content {
  text-align: center;
  position: relative;
  z-index: 2;
}

.mia-hero__icon {
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  backdrop-filter: blur(10px);
  animation: float 3s ease-in-out infinite;
}

.mia-hero__icon svg {
  color: white;
}

.mia-hero__title {
  color: white;
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 10px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.mia-hero__subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  font-weight: 400;
  margin: 0;
}

.mia-hero__decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.mia-hero__circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.mia-hero__circle--1 {
  width: 200px;
  height: 200px;
  top: -50px;
  right: -50px;
  animation: pulse 4s ease-in-out infinite;
}

.mia-hero__circle--2 {
  width: 150px;
  height: 150px;
  bottom: -30px;
  left: 10%;
  animation: pulse 5s ease-in-out infinite 1s;
}

.mia-hero__circle--3 {
  width: 100px;
  height: 100px;
  top: 30%;
  left: 5%;
  animation: pulse 6s ease-in-out infinite 2s;
}

.mia-hero__lang-switcher {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
}

.mia-page--rtl .mia-hero__lang-switcher {
  right: auto;
  left: 20px;
}

/* ========== Dashboard Container ========== */
.mia-dashboard {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px 60px;
}

.mia-dashboard--rtl {
  direction: rtl;
  text-align: right;
}

/* ========== Sections ========== */
.mia-section {
  margin-bottom: 40px;
}

.mia-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.mia-section__title {
  font-size: 20px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 12px 0;
}

.mia-section__info-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border: none;
  color: #00c444;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.mia-section__info-btn:hover {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  transform: scale(1.1);
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.3);
}

/* ========== Feature Cards Grid ========== */
.mia-features-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #e6ffee 0%, #d4f5e0 100%);
  border-radius: 24px;
  border: 1px solid #a8ebc4;
  box-shadow: 0 10px 40px rgba(0, 199, 68, 0.1);
}

.mia-feature-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  background: white;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 2px solid transparent;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.mia-feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 40px rgba(0, 199, 68, 0.2);
  border-color: #00c444;
}

.mia-feature-card__icon {
  color: #00c444;
  margin-bottom: 12px;
  transition: all 0.3s ease;
}

.mia-feature-card:hover .mia-feature-card__icon {
  transform: scale(1.15);
  color: #ffffffff;
}

.mia-feature-card__title {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  text-align: center;
  transition: all 0.3s ease;
}

.mia-feature-card:hover .mia-feature-card__title {
  color: #00c444;
}

/* ========== Input Cards Grid ========== */
.mia-input-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #e6ffee 0%, #d4f5e0 100%);
  border-radius: 24px;
  border: 1px solid #a8ebc4;
  box-shadow: 0 10px 40px rgba(0, 199, 68, 0.1);
}

.mia-input-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px;
  background: white;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.mia-input-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.15);
  border-color: #00c444;
}

.mia-input-card__icon {
  color: #00c444;
  margin-bottom: 10px;
  transition: all 0.3s ease;
}

.mia-input-card:hover .mia-input-card__icon {
  transform: scale(1.1);
  color: #00a83b;
}

.mia-input-card__title {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  transition: all 0.3s ease;
}

.mia-input-card:hover .mia-input-card__title {
  color: #00c444;
}

/* ========== Operations ========== */
.mia-operations {
  background: white;
  border-radius: 20px;
  min-height: 200px;
  border: 1px solid #c8f5d8;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
}

.mia-operations__loading,
.mia-operations__error,
.mia-operations__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  color: #6b7280;
}

.mia-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #c8f5d8;
  border-top-color: #00c444;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.mia-operations__list {
  padding: 12px;
}

.mia-operation-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #f0fff5 0%, #e6ffee 100%);
  border-radius: 14px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

.mia-operation-item:last-child {
  margin-bottom: 0;
}

.mia-operation-item:hover {
  background: white;
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.15);
  border-color: #a8ebc4;
}

.mia-operation-item__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 12px;
  color: white;
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.3);
  transition: all 0.3s ease;
}

.mia-operation-item:hover .mia-operation-item__icon {
  transform: scale(1.05);
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.4);
}

.mia-operation-item__content {
  flex: 1;
  min-width: 0;
}

.mia-operation-item__title {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.3s ease;
}

.mia-operation-item:hover .mia-operation-item__title {
  color: #00c444;
}

.mia-operation-item__date {
  font-size: 12px;
  color: #6b7280;
}

.mia-operation-item__status {
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  padding: 5px 12px;
  border-radius: 16px;
}

.mia-operation-item__status--completed {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
}
.mia-operation-item__status--processing {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}
.mia-operation-item__status--pending {
  color: #f97316;
  background: rgba(249, 115, 22, 0.1);
}
.mia-operation-item__status--failed {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

/* ========== Guest Card ========== */
.mia-guest-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 32px;
  text-align: center;
}

.mia-guest-card__image {
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
  filter: drop-shadow(0 0 30px rgba(0, 196, 68, 0.3));
  animation: float 3s ease-in-out infinite;
}

.mia-guest-card__title {
  font-size: 20px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 10px 0;
}

.mia-guest-card__text {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 20px 0;
}

.mia-guest-card__btn {
  padding: 14px 35px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.3);
}

.mia-guest-card__btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(0, 199, 68, 0.4);
}

/* ========== Dialog ========== */
.mia-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.mia-dialog {
  background: white;
  border-radius: 24px;
  padding: 32px;
  max-width: 420px;
  width: 100%;
  text-align: center;
  position: relative;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
  animation: bounceIn 0.4s ease;
}

.mia-dialog__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  margin: 0 auto 16px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 20px;
  color: #00c444;
}

.mia-dialog__title {
  font-size: 22px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 16px 0;
}

.mia-dialog__list {
  list-style: none;
  padding: 0;
  margin: 0 0 24px 0;
  text-align: left;
}

.mia-dialog__list li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 14px;
  font-size: 14px;
  color: #374151;
  background: #f0fff5;
  border-radius: 10px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
}

.mia-dialog__list li:hover {
  background: #e6ffee;
}

.mia-dialog__list li svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.mia-dialog__actions {
  display: flex;
  gap: 12px;
}

/* ========== Buttons ========== */
.mia-btn {
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.mia-btn--primary {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.3);
}

.mia-btn--gradient {
  flex: 1;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.3);
  color: white;
  border: none;
}

.mia-btn--gradient:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.4);
}

.mia-btn--icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border: none;
  border-radius: 12px;
  color: #00c444;
  transition: all 0.3s ease;
}

.mia-btn--icon:hover {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  transform: scale(1.05);
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.3);
}

/* ========== Responsive ========== */
@media (max-width: 768px) {
  .mia-hero {
    padding: 40px 20px;
    border-radius: 0 0 30px 30px;
  }

  .mia-hero__title {
    font-size: 1.8rem;
  }

  .mia-hero__icon {
    width: 80px;
    height: 80px;
  }

  .mia-dashboard {
    padding: 0 16px 40px;
  }

  .mia-features-grid,
  .mia-input-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
    padding: 18px;
  }

  .mia-feature-card,
  .mia-input-card {
    padding: 18px 12px;
  }

  .mia-section__title {
    font-size: 16px;
  }

  .mia-dialog {
    padding: 24px;
    margin: 16px;
  }

  .mia-btn {
    padding: 10px 20px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .mia-features-grid,
  .mia-input-grid {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 16px;
  }

  .mia-header__greeting {
    font-size: 24px;
  }

  .mia-header__subtitle {
    font-size: 16px;
  }
}

/* ========== DARK MODE SUPPORT ========== */
[data-bs-theme="dark"] .mia-page,
[data-theme="dark"] .mia-page,
.dark .mia-page {
  background: linear-gradient(135deg, #0f1419 0%, #1a2332 100%);
}

[data-bs-theme="dark"] .mia-hero,
[data-theme="dark"] .mia-hero,
.dark .mia-hero {
  background: linear-gradient(135deg, #00a83b 0%, #008f32 50%, #007a2b 100%);
}

[data-bs-theme="dark"] .mia-dashboard__section,
[data-theme="dark"] .mia-dashboard__section,
.dark .mia-dashboard__section {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .mia-section__title,
[data-theme="dark"] .mia-section__title,
.dark .mia-section__title {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .mia-feature-card,
[data-bs-theme="dark"] .mia-input-card,
[data-theme="dark"] .mia-feature-card,
[data-theme="dark"] .mia-input-card,
.dark .mia-feature-card,
.dark .mia-input-card {
  background: #0f172a;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .mia-feature-card:hover,
[data-bs-theme="dark"] .mia-input-card:hover,
[data-theme="dark"] .mia-feature-card:hover,
[data-theme="dark"] .mia-input-card:hover,
.dark .mia-feature-card:hover,
.dark .mia-input-card:hover {
  background: rgba(0, 196, 68, 0.1);
  border-color: #00c444;
}

[data-bs-theme="dark"] .mia-feature-card__title,
[data-bs-theme="dark"] .mia-input-card__title,
[data-theme="dark"] .mia-feature-card__title,
[data-theme="dark"] .mia-input-card__title,
.dark .mia-feature-card__title,
.dark .mia-input-card__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .mia-feature-card__desc,
[data-theme="dark"] .mia-feature-card__desc,
.dark .mia-feature-card__desc {
  color: #94a3b8;
}

[data-bs-theme="dark"] .mia-recent-ops,
[data-theme="dark"] .mia-recent-ops,
.dark .mia-recent-ops {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .mia-recent-ops__title,
[data-theme="dark"] .mia-recent-ops__title,
.dark .mia-recent-ops__title {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .mia-recent-ops__hint,
[data-theme="dark"] .mia-recent-ops__hint,
.dark .mia-recent-ops__hint {
  color: #64748b;
}

[data-bs-theme="dark"] .mia-op-card,
[data-theme="dark"] .mia-op-card,
.dark .mia-op-card {
  background: #0f172a;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .mia-op-card:hover,
[data-theme="dark"] .mia-op-card:hover,
.dark .mia-op-card:hover {
  background: rgba(0, 196, 68, 0.1);
}

[data-bs-theme="dark"] .mia-op-card__title,
[data-theme="dark"] .mia-op-card__title,
.dark .mia-op-card__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .mia-op-card__meta,
[data-theme="dark"] .mia-op-card__meta,
.dark .mia-op-card__meta {
  color: #64748b;
}

[data-bs-theme="dark"] .mia-empty,
[data-theme="dark"] .mia-empty,
.dark .mia-empty {
  color: #94a3b8;
}

[data-bs-theme="dark"] .mia-empty p,
[data-theme="dark"] .mia-empty p,
.dark .mia-empty p {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .mia-guest-card,
[data-theme="dark"] .mia-guest-card,
.dark .mia-guest-card {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.2);
}

[data-bs-theme="dark"] .mia-guest-card__title,
[data-theme="dark"] .mia-guest-card__title,
.dark .mia-guest-card__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .mia-guest-card__text,
[data-theme="dark"] .mia-guest-card__text,
.dark .mia-guest-card__text {
  color: #94a3b8;
}

[data-bs-theme="dark"] .mia-dialog,
[data-theme="dark"] .mia-dialog,
.dark .mia-dialog {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.2);
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
}

[data-bs-theme="dark"] .mia-dialog__title,
[data-theme="dark"] .mia-dialog__title,
.dark .mia-dialog__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .mia-dialog__list li,
[data-theme="dark"] .mia-dialog__list li,
.dark .mia-dialog__list li {
  background: #0f172a;
  color: #e2e8f0;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .mia-dialog__list li:hover,
[data-theme="dark"] .mia-dialog__list li:hover,
.dark .mia-dialog__list li:hover {
  background: rgba(0, 196, 68, 0.1);
}

[data-bs-theme="dark"] .mia-btn--icon,
[data-theme="dark"] .mia-btn--icon,
.dark .mia-btn--icon {
  background: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .mia-btn--icon:hover,
[data-theme="dark"] .mia-btn--icon:hover,
.dark .mia-btn--icon:hover {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
}

/* Fix for mia-features-grid dark mode */
[data-bs-theme="dark"] .mia-features-grid,
[data-theme="dark"] .mia-features-grid,
.dark .mia-features-grid {
  /* Grid layout is already responsive, just ensure proper spacing in dark mode */
  gap: 20px;
}

@media (max-width: 768px) {
  [data-bs-theme="dark"] .mia-features-grid,
  [data-theme="dark"] .mia-features-grid,
  .dark .mia-features-grid {
    gap: 14px;
  }
}

@media (max-width: 480px) {
  [data-bs-theme="dark"] .mia-features-grid,
  [data-theme="dark"] .mia-features-grid,
  .dark .mia-features-grid {
    gap: 12px;
  }
}

/* Fix for mia-operations and mia-input-grid dark mode */
[data-bs-theme="dark"] .mia-operations,
[data-theme="dark"] .mia-operations,
.dark .mia-operations {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .mia-operations__loading,
[data-bs-theme="dark"] .mia-operations__error,
[data-bs-theme="dark"] .mia-operations__empty,
[data-theme="dark"] .mia-operations__loading,
[data-theme="dark"] .mia-operations__error,
[data-theme="dark"] .mia-operations__empty,
.dark .mia-operations__loading,
.dark .mia-operations__error,
.dark .mia-operations__empty {
  color: #94a3b8;
}

[data-bs-theme="dark"] .mia-operations__loading span,
[data-bs-theme="dark"] .mia-operations__error span,
[data-bs-theme="dark"] .mia-operations__empty span,
[data-theme="dark"] .mia-operations__loading span,
[data-theme="dark"] .mia-operations__error span,
[data-theme="dark"] .mia-operations__empty span,
.dark .mia-operations__loading span,
.dark .mia-operations__error span,
.dark .mia-operations__empty span {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .mia-spinner,
[data-theme="dark"] .mia-spinner,
.dark .mia-spinner {
  border-color: #475569;
  border-top-color: #00c444;
}

/* mia-input-grid is already handled by mia-feature-card styles */
[data-bs-theme="dark"] .mia-input-grid,
[data-theme="dark"] .mia-input-grid,
.dark .mia-input-grid {
  /* Grid layout is already responsive, just ensure proper spacing in dark mode */
  gap: 20px;
}

@media (max-width: 768px) {
  [data-bs-theme="dark"] .mia-input-grid,
  [data-theme="dark"] .mia-input-grid,
  .dark .mia-input-grid {
    gap: 14px;
  }
}

@media (max-width: 480px) {
  [data-bs-theme="dark"] .mia-input-grid,
  [data-theme="dark"] .mia-input-grid,
  .dark .mia-input-grid {
    gap: 12px;
  }
}
</style>
